import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import API from '@Services/apiAxios';
import { SHIPMENT_STATUS } from '@Constants/Shipment';

interface UpdatedBy {
  _id: string;
  name: string;
  accessGroup: string;
}

interface ShipmentChange {
  oldData?: any;
  newData?: any;
}

interface PackageChange {
  oldData?: any;
  newData?: any;
}

interface ConsigneeChange {
  oldData?: any;
  newData?: any;
}

// NEW: Shipper change interface
interface ShipperChange {
  oldData?: any;
  newData?: any;
}

interface ShipmentHistoryItem {
  _id: string;
  _shipmentId: string;
  _updatedById?: UpdatedBy;
  shipment?: ShipmentChange;
  package?: PackageChange;
  consignee?: ConsigneeChange;
  shipper?: ShipperChange; // NEW: Added shipper field
  comment?: string;
  createdAt: string;
}

interface ShipmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',

  width: {
    xs: '95%',
    sm: '90%',
    md: '700px',
  },

  maxHeight: '85vh',
  overflowY: 'auto',

  // ✅ Glass Effect
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',

  background: 'rgba(255,255,255,0.72)',

  border: '1px solid rgba(255,255,255,0.35)',

  boxShadow: `
    0 8px 32px rgba(31, 38, 135, 0.18),
    inset 0 1px 0 rgba(255,255,255,0.35)
  `,

  // ✅ Rounded Corners
  borderRadius: '24px',

  p: 3,

  // ✅ Smooth scrolling
  scrollBehavior: 'smooth',

  // ✅ Nice scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.15)',
    borderRadius: '20px',
  },
};

const formatPhone = (phone: any) => {
  if (!phone) return 'Not set';

  if (typeof phone === 'object') {
    const code = phone.telephoneCode ?? '';
    const number = phone.number ?? '';
    return `${code} ${number}`.trim();
  }

  // fallback (string)
  return phone;
};

const ShipmentHistoryModal: React.FC<ShipmentHistoryModalProps> = ({ open, onClose, shipmentId }) => {
  const [history, setHistory] = useState<ShipmentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/shipment/admin/history/${shipmentId}`);
      const data: ShipmentHistoryItem[] = response.data.data;
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch shipment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipColor = (status: string): string => {
    if (!status) return '#E0E0E0';

    switch (status.toLowerCase()) {
      case 'placed':
        return '#E6E6FF';
      case 'at warehouse':
      case 'at_warehouse':
        return '#c4c4cfff';
      case 'in transit':
      case 'in_transit':
        return '#8cc49dff';
      case 'forwarded':
        return '#B2FFC9';
      case 'rto':
        return '#AAED66';
      case 'cancelled':
        return '#e4b6b6ff';
      case 'delivered':
        return '#80a88cff';
      case 'shipment exception':
      case 'shipment_exception':
        return '#fcadadff';
      case 'unsuccessful delivery':
      case 'unsuccessful_delivery':
        return '#f1afafff';
      default:
        return '#E0E0E0';
    }
  };

  const extractChargesMap = (chargesObj: any, type: 'selling' | 'buying' | 'purchase', kind: 'add' | 'discount') => {
    if (!chargesObj || typeof chargesObj !== 'object') return {};
    if (chargesObj[type] && typeof chargesObj[type] === 'object' && chargesObj[type][kind]) {
      return chargesObj[type][kind] || {};
    }
    if (chargesObj[kind] && typeof chargesObj[kind] === 'object') {
      return chargesObj[kind] || {};
    }
    return {};
  };

  const renderExtraChargesComparison = (oldCharges: any, newCharges: any, type: 'selling' | 'buying' | 'purchase') => {
    if (!newCharges) return null;

    const oldAdd = extractChargesMap(oldCharges, type, 'add');
    const newAdd = extractChargesMap(newCharges, type, 'add');

    const oldDiscount = extractChargesMap(oldCharges, type, 'discount');
    const newDiscount = extractChargesMap(newCharges, type, 'discount');

    const addKeys = new Set([...Object.keys(oldAdd), ...Object.keys(newAdd)]);
    const discountKeys = new Set([...Object.keys(oldDiscount), ...Object.keys(newDiscount)]);

    const addChanges: React.ReactNode[] = [];
    addKeys.forEach(key => {
      const oldValue = oldAdd[key]?.value;
      const newValue = newAdd[key]?.value;

      // If newData does not contain this charge (or it is null/undefined), do not render
      if (newValue === undefined || newValue === null) return;
      if (oldValue === newValue) return;

      addChanges.push(
        <Box key={`add-${key}`} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>{key}:</strong>
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {oldValue !== undefined && oldValue !== null ? (
              <Chip label={`${oldValue}`} size="small" color="default" variant="outlined" />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No previous value
              </Typography>
            )}

            <Typography variant="body2">→</Typography>

            <Chip label={`${newValue}`} size="small" color={type === 'selling' ? 'secondary' : 'primary'} />
          </Stack>
        </Box>,
      );
    });

    const discountChanges: React.ReactNode[] = [];
    discountKeys.forEach(key => {
      const oldValue = oldDiscount[key]?.value;
      const newValue = newDiscount[key]?.value;

      // If newData does not contain this discount charge (or it is null/undefined), do not render
      if (newValue === undefined || newValue === null) return;
      if (oldValue === newValue) return;

      discountChanges.push(
        <Box key={`discount-${key}`} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>{key} (Discount):</strong>
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {oldValue !== undefined && oldValue !== null ? (
              <Chip label={`${oldValue}`} size="small" color="default" variant="outlined" />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No previous value
              </Typography>
            )}

            <Typography variant="body2">→</Typography>

            <Chip label={`${newValue}`} size="small" color="warning" />
          </Stack>
        </Box>,
      );
    });

    if (addChanges.length === 0 && discountChanges.length === 0) return null;

    const titleLabel = type === 'selling' ? 'Selling' : type === 'buying' ? 'Buying' : 'Purchase';

    return (
      <Paper elevation={0} sx={{ p: 1, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {titleLabel} Extra Charges
        </Typography>
        <Divider sx={{ my: 1 }} />
        {addChanges}
        {discountChanges}
      </Paper>
    );
  };

  const getPricingArrayChanges = (oldPricing: any[] = [], newPricing: any[] = []) => {
    const normalizeKey = (item: any) =>
      `${(item.rateType || 'main').toLowerCase()}_${(item.key || item.displayKey || '')
        .toLowerCase()
        .replace(/[\s_-]+/g, '')}`;

    const oldMap = (oldPricing || []).reduce((acc, cur) => {
      if (cur) acc[normalizeKey(cur)] = cur;
      return acc;
    }, {} as Record<string, any>);

    const newMap = (newPricing || []).reduce((acc, cur) => {
      if (cur) acc[normalizeKey(cur)] = cur;
      return acc;
    }, {} as Record<string, any>);

    const allKeys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);
    const changes: React.ReactNode[] = [];

    allKeys.forEach(normKey => {
      const oldItem = oldMap[normKey];
      const newItem = newMap[normKey];

      // If newData does not contain this item (or it is null/undefined), do not render
      if (!newItem) return;

      // Item Added
      if (!oldItem && newItem) {
        if (newItem.value === undefined || newItem.value === null) return;

        const label = newItem.displayKey || newItem.key || 'Charge';
        const isExtra = newItem.rateType === 'extra';
        const rateLabel = isExtra ? 'Extra' : 'Main';

        changes.push(
          <Box key={normKey} sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>
                {label} ({rateLabel}):
              </strong>
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                No previous value
              </Typography>
              <Typography variant="body2">→</Typography>
              <Chip label={`${newItem.value ?? 0}`} size="small" color="success" />
              {newItem.invoiced !== undefined && (
                <Chip
                  label={newItem.invoiced ? 'Invoiced' : 'Uninvoiced'}
                  size="small"
                  color={newItem.invoiced ? 'success' : 'default'}
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>,
        );
        return;
      }

      // Both oldItem and newItem exist:
      if (oldItem && newItem) {
        // If identical value, invoiced, and shipmentNumber -> no change
        if (
          oldItem.value === newItem.value &&
          oldItem.invoiced === newItem.invoiced &&
          oldItem.shipmentNumber === newItem.shipmentNumber
        ) {
          return;
        }

        const label = newItem.displayKey || newItem.key || oldItem.displayKey || oldItem.key || 'Charge';
        const isExtra = newItem.rateType === 'extra' || oldItem.rateType === 'extra';
        const rateLabel = isExtra ? 'Extra' : 'Main';

        // Value changed
        if (oldItem.value !== newItem.value && newItem.value !== undefined && newItem.value !== null) {
          changes.push(
            <Box key={normKey} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>
                  {label} ({rateLabel}):
                </strong>
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {oldItem.value !== undefined && oldItem.value !== null ? (
                  <Chip label={`${oldItem.value ?? 0}`} size="small" variant="outlined" />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No previous value
                  </Typography>
                )}
                <Typography variant="body2">→</Typography>
                <Chip label={`${newItem.value ?? 0}`} size="small" color="success" />
                {newItem.invoiced !== undefined && (
                  <Chip
                    label={newItem.invoiced ? 'Invoiced' : 'Uninvoiced'}
                    size="small"
                    color={newItem.invoiced ? 'success' : 'default'}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>,
          );
          return;
        }

        // Invoiced / Debit note status changed (same value)
        if (oldItem.invoiced !== newItem.invoiced || oldItem.shipmentNumber !== newItem.shipmentNumber) {
          const oldInvoicedText = oldItem.invoiced ? 'Invoiced' : 'Uninvoiced';
          const newInvoicedText = newItem.invoiced
            ? newItem.shipmentNumber
              ? `Invoiced (${newItem.shipmentNumber})`
              : 'Invoiced'
            : 'Uninvoiced';

          changes.push(
            <Box key={normKey} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>
                  {label} ({rateLabel}):
                </strong>{' '}
                {newItem.value}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={oldInvoicedText} size="small" variant="outlined" />
                <Typography variant="body2">→</Typography>
                <Chip label={newInvoicedText} size="small" color={newItem.invoiced ? 'success' : 'default'} />
              </Stack>
            </Box>,
          );
        }
      }
    });

    return changes;
  };

  const renderPricingChanges = (oldPricing: any, newPricing: any, type: 'Selling' | 'Buying' | 'Purchase') => {
    // If newPricing is missing/null/undefined, do not render (per rule: oldData=value -> newData=null -> do not render)
    if (!newPricing) return null;

    // Case 1: Initial creation / Newly configured
    if (!oldPricing && newPricing) {
      return (
        <Paper elevation={0} sx={{ p: 1, mb: 2, backgroundColor: '#f5f5f5' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {type} Pricing
            </Typography>
            <Chip label="Configured" size="small" color="success" />
          </Stack>
          <Divider sx={{ my: 1 }} />

          {newPricing.serviceName && (
            <Typography variant="body2" mb={0.5}>
              <strong>Service Name:</strong> {newPricing.serviceName}
            </Typography>
          )}

          {newPricing.totalPricing !== undefined && newPricing.totalPricing !== null && (
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="body2">
                <strong>Total Pricing:</strong>
              </Typography>
              <Chip
                label={`${newPricing.currency ? newPricing.currency + ' ' : ''}${newPricing.totalPricing}`}
                size="small"
                color="success"
              />
            </Stack>
          )}

          {newPricing.chargeableWeight !== undefined && newPricing.chargeableWeight !== null && (
            <Typography variant="body2" mb={0.5}>
              <strong>Chargeable Weight:</strong> {newPricing.chargeableWeight} kg
            </Typography>
          )}

          {newPricing.pricing?.length > 0 && (
            <Box mt={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Pricing Breakdown:
              </Typography>
              <Box pl={1} mt={0.5}>
                {newPricing.pricing.map((item: any, idx: number) => {
                  const label = item.displayKey || item.key || `Item ${idx + 1}`;
                  const isExtra = item.rateType === 'extra';
                  return (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Typography variant="body2" sx={{ minWidth: 140 }}>
                        {label} ({isExtra ? 'Extra' : 'Main'}):
                      </Typography>
                      <Chip label={`${item.value ?? 0}`} size="small" variant="outlined" />
                      {item.invoiced !== undefined && (
                        <Chip
                          label={item.invoiced ? 'Invoiced' : 'Uninvoiced'}
                          size="small"
                          color={item.invoiced ? 'success' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          )}

          {renderExtraChargesComparison(null, newPricing.extraCharges, type.toLowerCase() as any)}
        </Paper>
      );
    }

    // Case 2: Both exist -> Compare differences (only when newData has a value)
    const changes: React.ReactNode[] = [];

    if (
      newPricing.serviceName !== undefined &&
      newPricing.serviceName !== null &&
      oldPricing.serviceName !== newPricing.serviceName
    ) {
      changes.push(
        <Box key="serviceName" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Service Name:</strong> {oldPricing.serviceName || 'Not set'} → {newPricing.serviceName}
          </Typography>
        </Box>,
      );
    }

    if (
      newPricing.totalPricing !== undefined &&
      newPricing.totalPricing !== null &&
      oldPricing.totalPricing !== newPricing.totalPricing
    ) {
      changes.push(
        <Box key="totalPricing" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Total Pricing:</strong>
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {oldPricing.totalPricing !== undefined && oldPricing.totalPricing !== null ? (
              <Chip label={`${oldPricing.totalPricing}`} size="small" color="default" variant="outlined" />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No previous value
              </Typography>
            )}

            <Typography variant="body2">→</Typography>

            <Chip
              label={`${newPricing.totalPricing}`}
              size="small"
              color={type === 'Selling' ? 'secondary' : 'primary'}
            />
          </Stack>
        </Box>,
      );
    }

    if (
      newPricing.chargeableWeight !== undefined &&
      newPricing.chargeableWeight !== null &&
      oldPricing.chargeableWeight !== newPricing.chargeableWeight
    ) {
      changes.push(
        <Box key="chargeableWeight" sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Chargeable Weight:</strong> {oldPricing.chargeableWeight ?? 'Not set'} kg →{' '}
            {newPricing.chargeableWeight} kg
          </Typography>
        </Box>,
      );
    }

    const lineItemChanges = getPricingArrayChanges(oldPricing.pricing || [], newPricing.pricing || []);
    if (lineItemChanges.length > 0) {
      changes.push(
        <Box key="pricingArray" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Pricing Breakdown Changes:
          </Typography>
          <Box pl={1} mt={0.5}>
            {lineItemChanges}
          </Box>
        </Box>,
      );
    }

    const extraChargesElement = renderExtraChargesComparison(
      oldPricing.extraCharges,
      newPricing.extraCharges,
      type.toLowerCase() as any,
    );

    if (changes.length === 0 && !extraChargesElement) {
      return null;
    }

    return (
      <Paper elevation={0} sx={{ p: 1, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {type} Pricing Changes
        </Typography>
        <Divider sx={{ my: 1 }} />
        {changes}
        {extraChargesElement}
      </Paper>
    );
  };

  const renderShipmentChanges = (shipment: ShipmentChange) => {
    if (!shipment?.oldData && !shipment?.newData) return null;

    const oldData = shipment.oldData || {};
    const newData = shipment.newData || {};

    return (
      <>
        {renderExtraChargesComparison(oldData.extraCharges, newData.extraCharges, 'selling')}
        {renderExtraChargesComparison(oldData.extraCharges, newData.extraCharges, 'buying')}

        {renderPricingChanges(oldData.sellingPricing, newData.sellingPricing, 'Selling')}
        {renderPricingChanges(oldData.buyingPricing, newData.buyingPricing, 'Buying')}
        {renderPricingChanges(oldData.purchasePricing, newData.purchasePricing, 'Purchase')}

        {oldData.shipmentMode && newData.shipmentMode && oldData.shipmentMode !== newData.shipmentMode && (
          <Typography variant="body2" mb={1}>
            <strong>Shipment Mode:</strong> {oldData.shipmentMode} → {newData.shipmentMode}
          </Typography>
        )}

        {oldData.serviceType && newData.serviceType && oldData.serviceType !== newData.serviceType && (
          <Typography variant="body2" mb={1}>
            <strong>Service Type:</strong> {oldData.serviceType} → {newData.serviceType}
          </Typography>
        )}
      </>
    );
  };

  const renderPackageChanges = (pkg: PackageChange) => {
    if (!pkg.oldData || !pkg.newData) return null;

    return (
      <>
        {pkg.oldData.pickupDate && pkg.newData.pickupDate && (
          <Typography variant="body2">
            <strong>Pickup Date:</strong> {new Date(pkg.oldData.pickupDate).toLocaleString()} →{' '}
            {new Date(pkg.newData.pickupDate).toLocaleString()}
          </Typography>
        )}

        {pkg.oldData.boxWeight !== undefined && pkg.newData.boxWeight !== undefined && (
          <Typography variant="body2">
            <strong>Box Weight:</strong> {pkg.oldData.boxWeight} → {pkg.newData.boxWeight}
          </Typography>
        )}

        {pkg.oldData.box?.length && pkg.newData.box?.length && (
          <>
            <Typography variant="body2" mt={1}>
              <strong>Box Changes:</strong>
            </Typography>
            {pkg.newData.box.map((box: any, index: number) => {
              const oldBox = pkg.oldData.box[index] || {};
              return (
                <Box key={index} sx={{ pl: 2, mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Box {index + 1}:</strong> {box.childAWBNumber || 'No AWB'}
                  </Typography>
                  {oldBox.weight !== undefined && box.weight !== undefined && (
                    <Typography variant="body2">
                      <strong>Weight:</strong> {oldBox.weight} → {box.weight}
                    </Typography>
                  )}
                  {box.products?.length > 0 && (
                    <Typography variant="body2">
                      <strong>Products:</strong> {box.products.length} {box.products.length > 1 ? 'Boxes' : 'Box'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </>
    );
  };

  const renderConsigneeChanges = (consignee: ConsigneeChange) => {
    if (!consignee.oldData || !consignee.newData) return null;

    return (
      <>
        {consignee.oldData.city && consignee.newData.city && (
          <Typography variant="body2">
            <strong>City:</strong> {consignee.oldData.city} → {consignee.newData.city}
          </Typography>
        )}

        {consignee.oldData.pincode && consignee.newData.pincode && (
          <Typography variant="body2">
            <strong>Pincode:</strong> {consignee.oldData.pincode} → {consignee.newData.pincode}
          </Typography>
        )}

        {consignee.oldData.address && consignee.newData.address && (
          <Typography variant="body2">
            <strong>Address:</strong> {consignee.oldData.address} → {consignee.newData.address}
          </Typography>
        )}

        {consignee.oldData.state && consignee.newData.state && (
          <Typography variant="body2">
            <strong>State:</strong> {consignee.oldData.state} → {consignee.newData.state}
          </Typography>
        )}

        {consignee.oldData.country && consignee.newData.country && (
          <Typography variant="body2">
            <strong>Country:</strong> {consignee.oldData.country} → {consignee.newData.country}
          </Typography>
        )}
      </>
    );
  };

  // NEW: Function to render shipper changes
  const renderShipperChanges = (shipper: ShipperChange) => {
    if (!shipper.oldData || !shipper.newData) return null;

    const changes = [];

    // Check for line1 changes
    if (
      shipper.newData.line1 !== undefined &&
      shipper.newData.line1 !== null &&
      shipper.oldData.line1 !== shipper.newData.line1
    ) {
      changes.push(
        <Typography key="line1" variant="body2">
          <strong>Line 1:</strong> {shipper.oldData.line1 || 'Not set'} → {shipper.newData.line1}
        </Typography>,
      );
    }

    // Check for other possible address fields
    const addressFields = ['line2', 'line3', 'city', 'state', 'pincode', 'country', 'address'];

    addressFields.forEach(field => {
      if (
        shipper.newData[field] !== undefined &&
        shipper.newData[field] !== null &&
        shipper.oldData[field] !== shipper.newData[field]
      ) {
        changes.push(
          <Typography key={field} variant="body2">
            <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong> {shipper.oldData[field] || 'Not set'} →{' '}
            {shipper.newData[field]}
          </Typography>,
        );
      }
    });

    // Check for name changes
    if (
      shipper.newData.name !== undefined &&
      shipper.newData.name !== null &&
      shipper.oldData.name !== shipper.newData.name
    ) {
      changes.push(
        <Typography key="name" variant="body2">
          <strong>Name:</strong> {shipper.oldData.name || 'Not set'} → {shipper.newData.name}
        </Typography>,
      );
    }

    // Check for phone/email changes
    if (
      shipper.newData.phone &&
      (shipper.oldData.phone?.number !== shipper.newData.phone?.number ||
        shipper.oldData.phone?.telephoneCode !== shipper.newData.phone?.telephoneCode)
    ) {
      changes.push(
        <Typography key="phone" variant="body2">
          <strong>Phone:</strong> {formatPhone(shipper.oldData.phone)} → {formatPhone(shipper.newData.phone)}
        </Typography>,
      );
    }

    if (
      shipper.newData.email !== undefined &&
      shipper.newData.email !== null &&
      shipper.oldData.email !== shipper.newData.email
    ) {
      changes.push(
        <Typography key="email" variant="body2">
          <strong>Email:</strong> {shipper.oldData.email || 'Not set'} → {shipper.newData.email}
        </Typography>,
      );
    }

    if (changes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No shipper changes detected
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Shipper Address Changes
        </Typography>
        {changes}
      </>
    );
  };

  const getChangeType = (item: ShipmentHistoryItem): string => {
    if (item.shipment) {
      if (item.shipment.oldData?.status && item.shipment.newData?.status) {
        return 'Status changed';
      }
      if (
        item.shipment.newData?.extraCharges ||
        item.shipment.oldData?.extraCharges ||
        item.shipment.newData?.buyingPricing ||
        item.shipment.oldData?.buyingPricing ||
        item.shipment.newData?.sellingPricing ||
        item.shipment.oldData?.sellingPricing ||
        item.shipment.newData?.purchasePricing ||
        item.shipment.oldData?.purchasePricing
      ) {
        return 'Pricing updated';
      }
      return 'Shipment updated';
    }
    if (item.package) return 'Package';
    if (item.consignee) return 'Consignee Address Update';
    if (item.shipper) return 'Shipper Address Update'; // NEW: Added shipper case
    if (item.comment) {
      const comment = item.comment.trim().toLowerCase();
      if (comment === 'invoice created, shipment locked.') {
        return 'Invoice created';
      }
      return item.comment;
    }
    return 'Update';
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2.5}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,

            backdropFilter: 'blur(16px)',
            background: 'rgba(255,255,255,0.45)',

            borderRadius: '18px',

            px: 2,
            py: 1.5,

            border: '1px solid rgba(255,255,255,0.25)',
          }}
        >
          <Typography variant="h6">Shipment History Logs</Typography>
          <IconButton
            onClick={onClose}
            sx={{
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.45)',
              backdropFilter: 'blur(10px)',

              border: '1px solid rgba(255,255,255,0.25)',

              transition: 'all 0.25s ease',

              '&:hover': {
                transform: 'rotate(90deg)',
                background: 'rgba(255,255,255,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : history?.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              No data found
            </Typography>
          </Box>
        ) : (
          history?.map(item => (
            <Accordion
              key={item._id}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.2,
                borderRadius: '18px !important',
                overflow: 'hidden',

                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',

                border: '1px solid rgba(255,255,255,0.25)',

                boxShadow: '0 4px 18px rgba(0,0,0,0.06)',

                transition: 'all 0.25s ease',

                '&:before': {
                  display: 'none',
                },

                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="body1">{getChangeType(item)}</Typography>
                  {item.shipment?.oldData?.status && item.shipment?.newData?.status && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.oldData.status]?.label || item.shipment.oldData.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusChipColor(item.shipment.oldData.status),
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />

                      <Typography fontSize={16} variant="body2">
                        →
                      </Typography>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.newData.status]?.label || item.shipment.newData.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusChipColor(item.shipment.newData.status),
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Stack>
                  )}
                  {item.package && <Chip label="Package" size="small" color="secondary" />}
                  {(item.shipment?.newData?.extraCharges ||
                    item.shipment?.oldData?.extraCharges ||
                    item.shipment?.newData?.purchasePricing?.extraCharges ||
                    item.shipment?.oldData?.purchasePricing?.extraCharges) && (
                    <Chip label="Extra Charges" color="primary" size="small" />
                  )}
                  {(item.shipment?.newData?.sellingPricing ||
                    item.shipment?.oldData?.sellingPricing ||
                    item.shipment?.newData?.buyingPricing ||
                    item.shipment?.oldData?.buyingPricing ||
                    item.shipment?.newData?.purchasePricing ||
                    item.shipment?.oldData?.purchasePricing ||
                    item.shipment?.newData) && (
                    <>
                      <Chip label="Pricing" color="success" size="small" />
                      {(item.comment === 'Masked Shipment.' || item.comment === 'Change Service.') && (
                        <Chip label={item?.comment} color="success" size="small" />
                      )}
                    </>
                  )}
                  {item.consignee && <Chip label="Consignee Address" color="warning" size="small" />}
                  {item.shipper && <Chip label="Shipper Address" color="info" size="small" />}{' '}
                  {/* NEW: Added shipper chip */}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {item._updatedById && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Updated By:</strong> {item._updatedById.name}
                  </Typography>
                )}
                {item.comment && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Comment:</strong> {item.comment}
                  </Typography>
                )}
                {item.shipment && renderShipmentChanges(item.shipment)}
                {item.package && renderPackageChanges(item.package)}
                {item.consignee && renderConsigneeChanges(item.consignee)}
                {item.shipper && renderShipperChanges(item.shipper)} {/* NEW: Render shipper changes */}
                <Typography variant="caption" display="block" mt={1}>
                  <strong>Date:</strong> {new Date(item.createdAt).toLocaleString()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </Modal>
  );
};

export default ShipmentHistoryModal;
